import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubItem,
    useSidebar,
} from '@app/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@app/components/ui/collapsible';
import { type NavItem } from '@app/types';
import { useTranslation } from 'react-i18next';
import { AnyLink as AppLink } from '@app/components/app-link';
import { ChevronRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

const matchesRoute = (url: string, pathname: string, search: string) => {
    const [itemPath, itemSearch] = url.split('?');
    if (itemSearch !== undefined) return url === `${pathname}${search}`;
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
};

// External entries leave the SPA, so they can never be the active route.
const collectUrls = (items: NavItem[]): string[] => items.flatMap((item) => [
    ...(typeof item.url === 'string' && !item.external ? [item.url] : []),
    ...(item.items ? collectUrls(item.items) : []),
]);

export function NavMain({ items = [] }: { items: NavItem[] }) {
    const { t } = useTranslation();
    const { pathname, search } = useLocation();
    const { isMobile, setOpenMobile } = useSidebar();
    const activeUrl = useMemo(
        () => collectUrls(items)
            .filter((url) => matchesRoute(url, pathname, search))
            .sort((a, b) => b.length - a.length)[0],
        [items, pathname, search],
    );

    const itemIsActive = (item: NavItem): boolean =>
        (typeof item.url === 'string' && !item.external && item.url === activeUrl) ||
        Boolean(item.items?.some(itemIsActive));

    const closeMobileSidebar = () => {
        if (isMobile) setOpenMobile(false);
    };

    const renderItems = (navItems: NavItem[], nested = false) => navItems.map((item) => {
        const hasChildren = Boolean(item.items?.length);
        const active = itemIsActive(item);
        const Item = nested ? SidebarMenuSubItem : SidebarMenuItem;

        return (
            <Collapsible key={item.id} asChild defaultOpen={active} className="group/collapsible">
                <Item>
                    {item.url ? (
                        <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                            {item.external && typeof item.url === 'string' ? (
                                // Not a react-router route — routing to it
                                // client-side renders a blank page, so use a
                                // real anchor and give it its own tab.
                                <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMobileSidebar}
                                >
                                    {item.icon ? <item.icon /> : null}
                                    <span>{item.title}</span>
                                </a>
                            ) : (
                                <AppLink href={item.url} onClick={closeMobileSidebar}>
                                    {item.icon ? <item.icon /> : null}
                                    <span>{item.title}</span>
                                </AppLink>
                            )}
                        </SidebarMenuButton>
                    ) : (
                        <CollapsibleTrigger asChild disabled={!hasChildren}>
                            <SidebarMenuButton isActive={active} tooltip={item.title}>
                                {item.icon ? <item.icon /> : null}
                                <span>{item.title}</span>
                                {hasChildren ? <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" /> : null}
                            </SidebarMenuButton>
                        </CollapsibleTrigger>
                    )}
                    {item.url && hasChildren ? (
                        <CollapsibleTrigger asChild>
                            <SidebarMenuAction aria-label={t('common.submenu', {title: item.title})}>
                                <ChevronRight className="transition-transform group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuAction>
                        </CollapsibleTrigger>
                    ) : null}
                    {hasChildren ? (
                        <CollapsibleContent>
                            <SidebarMenuSub>{renderItems(item.items!, true)}</SidebarMenuSub>
                        </CollapsibleContent>
                    ) : null}
                </Item>
            </Collapsible>
        );
    });

    return (
        <SidebarGroup className="px-2 py-0">
            <SidebarGroupLabel>{t('components.sidebar.platform')}</SidebarGroupLabel>
            <SidebarMenu>{renderItems(items)}</SidebarMenu>
        </SidebarGroup>
    );
}
